import React, { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../api';
import { PageHeader, EmptyState, TableSkeleton, StatusBadge } from '../../components/ui';
import {
  Plus, Pencil, Trash2, Layers, Heart, Briefcase, HeartHandshake, Activity,
  TrendingUp, Moon, Hash, LayoutGrid, Home, User, Star, Sparkles, Compass,
  Sun, Shield, type LucideIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';

const ICON_MAP: Record<string, LucideIcon> = {
  heart: Heart,
  briefcase: Briefcase,
  'heart-handshake': HeartHandshake,
  activity: Activity,
  'trending-up': TrendingUp,
  moon: Moon,
  hash: Hash,
  'layout-grid': LayoutGrid,
  home: Home,
  user: User,
  star: Star,
  sparkles: Sparkles,
  compass: Compass,
  sun: Sun,
  shield: Shield,
};

const ICON_OPTIONS = Object.keys(ICON_MAP);

function CategoryIcon({ name, color, size = 18 }: { name?: string; color?: string; size?: number }) {
  const Icon = (name && ICON_MAP[name]) || Layers;
  const bg = color ? `${color}22` : '#FF450022';
  const fg = color || '#FF4500';
  return (
    <div
      className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 border border-cream-300"
      style={{ background: bg, color: fg }}
      title={name || 'category'}
    >
      <Icon size={size} strokeWidth={2} />
    </div>
  );
}

const defaultForm = {
  slug: '',
  name_en: '',
  name_hi: '',
  icon: 'star',
  color: '#FF4500',
  order: 0,
  isActive: true,
};

export default function CategoriesPage() {
  const qc = useQueryClient();
  const [modal, setModal] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form, setForm] = useState(defaultForm);

  const { data, isLoading } = useQuery({
    queryKey: ['admin-categories'],
    queryFn: () => getCategories().then((r) => r.data.data.categories),
  });

  const saveMutation = useMutation({
    mutationFn: (payload: any) => (editing ? updateCategory(editing._id, payload) : createCategory(payload)),
    onSuccess: () => {
      toast.success(editing ? 'Category updated' : 'Category created');
      qc.invalidateQueries({ queryKey: ['admin-categories'] });
      closeModal();
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Save failed'),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => deleteCategory(id),
    onSuccess: () => {
      toast.success('Category deleted');
      qc.invalidateQueries({ queryKey: ['admin-categories'] });
    },
    onError: (e: any) => toast.error(e?.response?.data?.message || 'Delete failed'),
  });

  const openCreate = () => {
    setEditing(null);
    setForm({ ...defaultForm, order: (categories.length + 1) * 10 });
    setModal(true);
  };

  const openEdit = (cat: any) => {
    setEditing(cat);
    setForm({
      slug: cat.slug,
      name_en: cat.name?.en || '',
      name_hi: cat.name?.hi || '',
      icon: cat.icon || 'star',
      color: cat.color || '#FF4500',
      order: cat.order || 0,
      isActive: cat.isActive !== false,
    });
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
    setEditing(null);
    setForm(defaultForm);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate({
      slug: form.slug.trim().toLowerCase().replace(/\s+/g, '-'),
      name: { en: form.name_en.trim(), hi: form.name_hi.trim() },
      icon: form.icon,
      color: form.color,
      order: Number(form.order) || 0,
      isActive: form.isActive,
    });
  };

  const categories = useMemo(
    () => [...(data || [])].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0)),
    [data],
  );

  return (
    <div className="p-6">
      <PageHeader title="Services & Categories" subtitle="Speciality categories shown on home and filters">
        <button onClick={openCreate} className="btn-primary">
          <Plus size={15} /> Add Category
        </button>
      </PageHeader>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="p-5"><TableSkeleton rows={6} cols={5} /></div>
        ) : categories.length === 0 ? (
          <EmptyState message="No categories yet" icon={<Layers size={32} />} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px]">
              <thead className="bg-cream-100 border-b border-cream-300">
                <tr>
                  <th className="table-th w-14">#</th>
                  <th className="table-th">Category</th>
                  <th className="table-th">Slug</th>
                  <th className="table-th">Icon</th>
                  <th className="table-th">Status</th>
                  <th className="table-th text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat: any) => (
                  <tr key={cat._id} className="table-row">
                    <td className="table-td text-xs text-ink/40 font-mono">{cat.order ?? '—'}</td>
                    <td className="table-td">
                      <div className="flex items-center gap-3 min-w-0">
                        <CategoryIcon name={cat.icon} color={cat.color} />
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-ink truncate">{cat.name?.en || cat.slug}</p>
                          {cat.name?.hi ? (
                            <p className="text-xs text-ink/50 truncate">{cat.name.hi}</p>
                          ) : null}
                        </div>
                      </div>
                    </td>
                    <td className="table-td">
                      <code className="text-xs font-mono text-ink/70 bg-cream-100 px-2 py-0.5 rounded">{cat.slug}</code>
                    </td>
                    <td className="table-td">
                      <span className="text-xs text-ink/60 font-mono">{cat.icon || '—'}</span>
                    </td>
                    <td className="table-td">
                      <StatusBadge status={cat.isActive ? 'active' : 'inactive'} />
                    </td>
                    <td className="table-td">
                      <div className="flex justify-end gap-1">
                        <button
                          type="button"
                          onClick={() => openEdit(cat)}
                          className="p-2 rounded-lg hover:bg-cream-200 text-primary-600"
                          title="Edit"
                        >
                          <Pencil size={14} />
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            if (window.confirm(`Delete “${cat.name?.en || cat.slug}”?`)) {
                              deleteMutation.mutate(cat._id);
                            }
                          }}
                          className="p-2 rounded-lg hover:bg-primary-50 text-primary-700"
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 bg-ink/40 flex items-center justify-center z-50 p-4">
          <div className="bg-cream-50 rounded-2xl shadow-xl border border-cream-300 p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center gap-3 mb-5">
              <CategoryIcon name={form.icon} color={form.color} size={20} />
              <div>
                <h3 className="font-bold text-ink">{editing ? 'Edit category' : 'Add category'}</h3>
                <p className="text-xs text-ink/50">Icon + color appear in the user app filters</p>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-ink/70 mb-1">Slug *</label>
                  <input
                    className="input font-mono"
                    value={form.slug}
                    onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    placeholder="love"
                    required
                    disabled={!!editing}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-ink/70 mb-1">Order</label>
                  <input
                    type="number"
                    className="input"
                    value={form.order}
                    onChange={(e) => setForm({ ...form, order: Number(e.target.value) })}
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium text-ink/70 mb-1">Name (English) *</label>
                <input
                  className="input"
                  value={form.name_en}
                  onChange={(e) => setForm({ ...form, name_en: e.target.value })}
                  placeholder="Love & Relationship"
                  required
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-ink/70 mb-1">Name (Hindi)</label>
                <input
                  className="input"
                  value={form.name_hi}
                  onChange={(e) => setForm({ ...form, name_hi: e.target.value })}
                  placeholder="प्यार और रिश्ते"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-ink/70 mb-2">Icon</label>
                <div className="grid grid-cols-5 sm:grid-cols-6 gap-2">
                  {ICON_OPTIONS.map((key) => {
                    const selected = form.icon === key;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setForm({ ...form, icon: key })}
                        className={`flex flex-col items-center gap-1 p-2 rounded-xl border transition-all ${
                          selected
                            ? 'border-primary-500 bg-primary-50 ring-2 ring-primary-500/30'
                            : 'border-cream-300 hover:border-primary-300 bg-white'
                        }`}
                        title={key}
                      >
                        <CategoryIcon name={key} color={form.color} size={16} />
                        <span className="text-[9px] text-ink/50 truncate w-full text-center">{key}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 items-end">
                <div>
                  <label className="block text-xs font-medium text-ink/70 mb-1">Color</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      className="h-10 w-12 rounded-lg border border-cream-300 cursor-pointer bg-white p-1"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                    />
                    <input
                      className="input font-mono uppercase"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2 text-sm pb-2">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div className="flex gap-2 pt-2">
                <button type="button" onClick={closeModal} className="btn-secondary flex-1 justify-center">
                  Cancel
                </button>
                <button type="submit" disabled={saveMutation.isPending} className="btn-primary flex-1 justify-center">
                  {saveMutation.isPending ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
